/**
 * wiredProviders — provider ids the deployment can actually call.
 *
 * The preflight check (lib/preflightCheck.ts) routes its bootstrap-mode
 * red-vs-amber decision through this set: a missing section whose
 * primary provider is NOT wired stays red regardless of bootstrap
 * mode, because the upcoming run can't fix it — admin needs to
 * configure the provider first.
 *
 * The full provider list mirrors the keys in `SECTION_PRIMARY_PROVIDER`
 * (lib/preflightCheck.ts) — keep them aligned. A new section that
 * adds a primary-provider entry should also appear here so the
 * default "everything wired" baseline holds for fresh deployments.
 *
 * Future tightening: read tenant-level disabled providers off the
 * store (companies/{id}.settings.disabledProviders[]) and subtract
 * them from the default set. For now the function always returns
 * the full default — the spec just calls for the wizard to thread
 * the set through; a client-visible "this provider is off" signal
 * arrives in a follow-up alongside the AdminPanel toggle.
 */

const ALL_WIRED_PROVIDERS: ReadonlyArray<string> = [
  'open-meteo',
  'usgs-seismic',
  'fema-flood',
  'hifld-grid',
  'usfws-env',
  'wri-aqueduct',
  'eia-electricity',
  'airnow-aqi',
  'nrel-solar',
  'nrel-wind',
  'nrc-reactors',
  'peeringdb-ix',
  'site-research',
  'regrid-parcels',
  'eia-natural-gas',
  // RD-1 (D) — refdata-only providers (nationwide GCS snapshots).
  'epa-frs',
  'usgs-waters',
  'padus-protected',
  'census-aiannh',
];

export function getWiredProviders(): Set<string> {
  return new Set(ALL_WIRED_PROVIDERS);
}

/** Test/admin override hook — replaces the default set for the
 *  duration of a single test or for a deployment that wants to
 *  simulate "solar key not configured" without rebuilding. */
let _override: Set<string> | null = null;

export function setWiredProvidersOverride(set: Set<string> | null): void {
  _override = set;
}

export function getEffectiveWiredProviders(): Set<string> {
  return _override ?? getWiredProviders();
}
